import React, { useContext, useState } from "react";
import {
  MdLocationOn,
  MdOutlineAccessTimeFilled,
  MdClose,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FetchJobdata } from "../hooks/getJobData";

const JobListView = ({ jobList }) => {
  let navigate = useNavigate();
  const appURL = process.env.REACT_APP_JOB;

  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    Name: "",
    contact: "",
    email: "",
    Message: "",
    CVType: "U", // U for URL, F for File
    CV: "",
    CVUrl: "",
    CVFile: null,
    CoverType: "F", // U for URL, F for File
    Cover: "",
    CoverUrl: "",
    CoverFile: null,
    Via: "M"
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});


  const handleApplyClick = (e, jobData) => {
    e.stopPropagation(); // Prevent navigation to job page
    setSelectedJob(jobData);
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];
      if (file) {
        // Validate file size (2MB)
        if (file.size > 2 * 1024 * 1024) {
          setErrors({
            ...errors,
            [name]: "File size should be less than 2MB"
          });
          return;
        }

        // Validate file type
        const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!validTypes.includes(file.type)) {
          setErrors({
            ...errors,
            [name]: "Please upload PDF, DOC, or DOCX file"
          });
          return;
        }

        // Convert file to base64
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData({
            ...formData,
            [name]: reader.result,
            [`${name}File`]: file,
          });
        };
        reader.readAsDataURL(file);
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ""
      });
    }
  };

  const handleCVTypeChange = (type) => {
    setFormData({
      ...formData,
      CVType: type,
      CV: "",
      CVUrl: "",
      CVFile: null
    });
    // Clear CV related errors
    if (errors.CV) {
      setErrors({
        ...errors,
        CV: ""
      });
    }
  };

  const handleCoverTypeChange = (type) => {
    setFormData({
      ...formData,
      CoverType: type,
      Cover: "",
      CoverUrl: "",
      CoverFile: null
    });
    // Clear Cover related errors
    if (errors.Cover) {
      setErrors({
        ...errors,
        Cover: ""
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.Name.trim()) {
      newErrors.Name = "Name is required";
    }

    if (!formData.contact.trim()) {
      newErrors.contact = "Contact number is required";
    } else if (!/^\d{10}$/.test(formData.contact)) {
      newErrors.contact = "Please enter a valid 10-digit contact number";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Validate CV based on type
    if (formData.CVType === "U") {
      if (!formData.CVUrl.trim()) {
        newErrors.CV = "CV URL is required";
      } else if (!/^https?:\/\/.+/.test(formData.CVUrl)) {
        newErrors.CV = "Please enter a valid URL starting with http:// or https://";
      }
    } else if (formData.CVType === "F") {
      if (!formData.CVFile) {
        newErrors.CV = "CV file is required";
      }
    }

    // Validate Cover Letter based on type (if provided)
    if (formData.CoverType === "U" && formData.CoverUrl.trim()) {
      if (!/^https?:\/\/.+/.test(formData.CoverUrl)) {
        newErrors.Cover = "Please enter a valid URL starting with http:// or https://";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const params = {
      AuthCode: "r1d3r",
      ComID: "ES25",
      Flag: "i",
      UserID: "-1",
      JobID: selectedJob.JobsID?.toString(),
      Name: formData.Name,
      contact: formData.contact,
      email: formData.email,
      Message: formData.Message,
      CVType: formData.CVType,
      CV: formData.CVType === "U" ? formData.CVUrl : formData?.CV?.split(",")[1],
      CoverType: formData.CoverType,
      Cover: formData.CoverType === "U" ? formData.CoverUrl : formData.Cover?.split(",")[1],
      Via: "M",
      Type: "POST",
      FetchURL: `${appURL}api/job/apply`,
    };

    FetchJobdata(params).then(function (result) {
      if (result.StatusCode === 200) {
        toast.success(result.message || "Application submitted successfully");
        setShowModal(false);
        setLoading(false);
        resetForm();
      } else {
        toast.error(result.message || "Failed to submit application");
        setLoading(false);
      }
    })
      .catch((err) => {
        console.error("API request failed", err);
        toast.error("Failed to submit application");
      });
  };

  const resetForm = () => {
    setFormData({
      Name: "",
      contact: "",
      email: "",
      Message: "",
      CVType: "U",
      CV: "",
      CVUrl: "",
      CVFile: null,
      CoverType: "F",
      Cover: "",
      CoverUrl: "",
      CoverFile: null,
      Via: "M"
    });
    setErrors({});
    setSelectedJob(null);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  return (
    <>
      {jobList.map((jobData) => {
        // to show when was job created
        const createdDate = new Date(jobData.JobAddedDate);

        // Get the current date
        const currentDate = new Date();

        // Calculate the remaining days
        const timeDifference = createdDate.getTime() - currentDate.getTime();

        let daysAgo;

        if (createdDate > currentDate) {
          daysAgo = "";
        } else {
          daysAgo = Math.ceil(timeDifference / (1000 * 3600 * 24));
        }

        return (
          <div key={jobData.JobsID}>
            <div
              className="jobListsCard"
              style={{ cursor: "pointer" }}
            >
              <div className="job__wrapper">
                <div className="com__logo">
                  <img src={jobData.ComLogo} alt="logo" />
                </div>

                <div className="job__content">
                  <h2
                    className="fw-bold m-0 fs-4 lh-5"
                    style={{ color: "#000" }}
                  >
                    {jobData.Designation}
                  </h2>

                  <div className="com__info">{jobData.ComName}</div>

                  <div className="job__txt mt-1">
                    <MdLocationOn
                      color="#0b4881"
                      size="1rem"
                      className="me-1"
                    />{" "}
                    {jobData.ComAddress}
                  </div>

                  {/* <div className="badge__wrapper">
                    <div className="job__badge">{jobData.JobType}</div>
                    <div className="job__badge">{jobData.WorkType}</div>
                    {daysAgo !== "" && (
                      <div className="job__txt">
                        <MdOutlineAccessTimeFilled
                          color="#838897"
                          size="1rem"
                          className="me-1"
                        />{" "}
                        {daysAgo === -1 ? (
                          <span>{Math.abs(daysAgo)} day ago</span>
                        ) : daysAgo === 0 ? (
                          <span>Today</span>
                        ) : (
                          <span>{Math.abs(daysAgo)} days ago</span>
                        )}
                      </div>
                    )}
                  </div> */}

                  <div className="deadline__wrapper">
                    <div className="job__deadline">
                      Apply before: {jobData.Deadline}
                    </div>
                    <div className="job__deadline">
                      {jobData.DaysRemaining === 0 ? "" : jobData.DaysRemaining}{" "}
                      {jobData.DaysRemaining === "1"
                        ? "day remaining"
                        : jobData.DaysRemaining === 0
                          ? "Last day"
                          : "days remaining"}
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="uk-flex uk-flex-between uk-flex-middle mt-3"
                style={{ borderTop: "1px solid #eee" }}
              >
                <div className="job__deadline mt-2">
                  <MdOutlineAccessTimeFilled
                    color="#0b4881"
                    size="1rem"
                    style={{ marginTop: "-2px" }}
                  />{" "}
                  {jobData.DaysRemaining < 0
                    ? "Expired"
                    : jobData.DaysRemaining === 0
                      ? ""
                      : jobData.DaysRemaining}{" "}
                  {jobData.DaysRemaining === "1"
                    ? "day remaining"
                    : jobData.DaysRemaining === 0
                      ? "Last day"
                      : jobData.DaysRemaining < 0
                        ? ""
                        : "days remaining"}
                </div>

                <div className="badge__wrapper">
                  <div className="job__badge">{jobData.JobType}</div>
                  <div className="job__badge">{jobData.WorkType}</div>
                </div>

                {/* Apply Button */}
                <button
                  className="btn btn-primary mt-3"
                  onClick={(e) => handleApplyClick(e, jobData)}
                  disabled={jobData.DaysRemaining < 0}
                  style={{
                    background: "#0b4881",
                    color: "white",
                    padding: "8px 15px",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontWeight: "500"
                  }}
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {/* Modal Popup */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
          onClick={closeModal}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "10px",
              width: "90%",
              maxWidth: "600px",
              maxHeight: "90vh",
              overflowY: "auto",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: "20px",
                borderBottom: "1px solid #eee",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                position: "sticky",
                top: 0,
                backgroundColor: "white",
                zIndex: 1,
              }}
            >
              <div>
                <h3 style={{ margin: 0, color: "#0b4881" }}>Apply for Job</h3>
                {selectedJob && (
                  <p style={{ margin: "5px 0 0", fontSize: "14px", color: "#666" }}>
                    {selectedJob.Designation} - {selectedJob.ComName}
                  </p>
                )}
              </div>
              <MdClose
                size={24}
                style={{ cursor: "pointer", color: "#666" }}
                onClick={closeModal}
              />
            </div>

            {/* Modal Body - Form */}
            <form onSubmit={handleSubmit} style={{ padding: "20px" }}>
              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
                  Full Name *
                </label>
                <input
                  type="text"
                  name="Name"
                  value={formData.Name}
                  onChange={handleInputChange}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: `1px solid ${errors.Name ? "#dc3545" : "#ddd"}`,
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
                {errors.Name && (
                  <small style={{ color: "#dc3545", fontSize: "12px" }}>{errors.Name}</small>
                )}
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
                  Contact Number *
                </label>
                <input
                  type="tel"
                  name="contact"
                  value={formData.contact}
                  onChange={handleInputChange}
                  placeholder="10-digit mobile number"
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: `1px solid ${errors.contact ? "#dc3545" : "#ddd"}`,
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
                {errors.contact && (
                  <small style={{ color: "#dc3545", fontSize: "12px" }}>{errors.contact}</small>
                )}
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
                  Email Address *
                </label>
                <input
                  // type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: `1px solid ${errors.email ? "#dc3545" : "#ddd"}`,
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
                {errors.email && (
                  <small style={{ color: "#dc3545", fontSize: "12px" }}>{errors.email}</small>
                )}
              </div>

              {/* CV Section */}
              <div style={{ marginBottom: "20px", borderBottom: "1px solid #eee", paddingBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "10px", fontWeight: "600", color: "#0b4881" }}>
                  Curriculum Vitae (CV) *
                </label>

                {/* CV Type Toggle */}
                <div style={{ display: "flex", gap: "15px", marginBottom: "15px" }}>
                  <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="CVTypeRadio"
                      checked={formData.CVType === "U"}
                      onChange={() => handleCVTypeChange("U")}
                      style={{ marginRight: "5px" }}
                    />
                    URL Link
                  </label>
                  <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="CVTypeRadio"
                      checked={formData.CVType === "F"}
                      onChange={() => handleCVTypeChange("F")}
                      style={{ marginRight: "5px" }}
                    />
                    Upload File
                  </label>
                </div>

                {/* CV URL Input */}
                {formData.CVType === "U" && (
                  <div>
                    <input
                      type="url"
                      name="CVUrl"
                      value={formData.CVUrl}
                      onChange={handleInputChange}
                      placeholder="https://example.com/my-cv.pdf"
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: `1px solid ${errors.CV ? "#dc3545" : "#ddd"}`,
                        borderRadius: "4px",
                        fontSize: "14px",
                      }}
                    />
                    <small style={{ color: "#666", fontSize: "11px" }}>
                      Enter a valid URL to your CV (PDF, DOC, or DOCX)
                    </small>
                  </div>
                )}

                {/* CV File Upload */}
                {formData.CVType === "F" && (
                  <div>
                    <input
                      type="file"
                      name="CV"
                      onChange={handleInputChange}
                      accept=".pdf,.doc,.docx"
                      style={{
                        width: "100%",
                        padding: "5px",
                        border: `1px solid ${errors.CV ? "#dc3545" : "#ddd"}`,
                        borderRadius: "4px",
                        fontSize: "14px",
                      }}
                    />
                    <small style={{ color: "#666", fontSize: "11px" }}>
                      Allowed: PDF, DOC, DOCX (Max 2MB)
                    </small>
                  </div>
                )}

                {errors.CV && (
                  <small style={{ color: "#dc3545", fontSize: "12px", display: "block", marginTop: "5px" }}>
                    {errors.CV}
                  </small>
                )}
              </div>

              {/* Cover Letter Section */}
              <div style={{ marginBottom: "20px", borderBottom: "1px solid #eee", paddingBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "10px", fontWeight: "600", color: "#0b4881" }}>
                  Cover Letter (Optional)
                </label>

                {/* Cover Letter Type Toggle */}
                <div style={{ display: "flex", gap: "15px", marginBottom: "15px" }}>
                  <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="CoverTypeRadio"
                      checked={formData.CoverType === "U"}
                      onChange={() => handleCoverTypeChange("U")}
                      style={{ marginRight: "5px" }}
                    />
                    URL Link
                  </label>
                  <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="CoverTypeRadio"
                      checked={formData.CoverType === "F"}
                      onChange={() => handleCoverTypeChange("F")}
                      style={{ marginRight: "5px" }}
                    />
                    Upload File
                  </label>
                </div>

                {/* Cover Letter URL Input */}
                {formData.CoverType === "U" && (
                  <div>
                    <input
                      type="url"
                      name="CoverUrl"
                      value={formData.CoverUrl}
                      onChange={handleInputChange}
                      placeholder="https://example.com/cover-letter.pdf"
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: `1px solid ${errors.Cover ? "#dc3545" : "#ddd"}`,
                        borderRadius: "4px",
                        fontSize: "14px",
                      }}
                    />
                    <small style={{ color: "#666", fontSize: "11px" }}>
                      Enter a valid URL to your cover letter (Optional)
                    </small>
                  </div>
                )}

                {/* Cover Letter File Upload */}
                {formData.CoverType === "F" && (
                  <div>
                    <input
                      type="file"
                      name="Cover"
                      onChange={handleInputChange}
                      accept=".pdf,.doc,.docx"
                      style={{
                        width: "100%",
                        padding: "5px",
                        border: `1px solid ${errors.Cover ? "#dc3545" : "#ddd"}`,
                        borderRadius: "4px",
                        fontSize: "14px",
                      }}
                    />
                    <small style={{ color: "#666", fontSize: "11px" }}>
                      Allowed: PDF, DOC, DOCX (Max 2MB, Optional)
                    </small>
                  </div>
                )}

                {errors.Cover && (
                  <small style={{ color: "#dc3545", fontSize: "12px", display: "block", marginTop: "5px" }}>
                    {errors.Cover}
                  </small>
                )}
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
                  Additional Message (Optional)
                </label>
                <textarea
                  name="Message"
                  value={formData.Message}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Any additional information you'd like to share..."
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "14px",
                    resize: "vertical",
                  }}
                />
              </div>

              {/* Hidden fields */}
              <input type="hidden" name="Via" value={formData.Via} />

              {/* Modal Footer */}
              <div
                style={{
                  marginTop: "20px",
                  paddingTop: "20px",
                  borderTop: "1px solid #eee",
                  display: "flex",
                  gap: "10px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  type="button"
                  onClick={closeModal}
                  style={{
                    padding: "8px 20px",
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: "8px 20px",
                    backgroundColor: "#0b4881",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  {loading ? "Submitting..." : "Submit Application"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Styles */}
      <style jsx>{`
        @keyframes modalFade {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        div[style*="position: fixed"] > div {
          animation: modalFade 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default JobListView;